/**
 * Escape stack — a single document-level Escape listener shared by every
 * layered surface (`Drawer`, `ResponsivePopover`) so that when one is nested
 * inside another (e.g. `ViewNameDialog`'s `ResponsivePopover` opened from
 * inside `BrowseAllModal`'s `Drawer`), a single Escape keypress closes only
 * the TOPMOST layer instead of every registered listener firing at once.
 *
 * ── The bug this exists to fix (R2) ──────────────────────────────────────
 * `Drawer` and `ResponsivePopover` each registered their OWN raw
 * `document.addEventListener('keydown', ...)` that called their own
 * `onClose` unconditionally on Escape. Both listeners fire on a single
 * keypress — DOM event listeners don't know about each other — so pressing
 * Escape while a `ResponsivePopover`-based dialog was open INSIDE a `Drawer`
 * closed both in one keystroke. This module centralizes "who owns this
 * Escape" into a stack: only the most-recently-registered (topmost) layer's
 * callback runs.
 *
 * ── Single-layer callers are unaffected ──────────────────────────────────
 * `ResponsivePopover` is used all over the app standalone (not nested inside
 * a `Drawer`); when it is the only registered layer, it's also the top of
 * the stack, so Escape closes it exactly as before. Nothing changes for the
 * common case — this only changes behavior when TWO layers are open at once.
 *
 * ── StrictMode resilience ─────────────────────────────────────────────────
 * `pushEscapeLayer` is designed to be called from a `useEffect` and its
 * returned unregister function from that effect's cleanup. StrictMode's dev
 * double-invoke (setup→cleanup→setup) pushes a layer, removes it BY ITS OWN
 * ID (never "pop whatever is on top"), then pushes a fresh one — so the two
 * cycles net out to exactly one registered layer, with no stale duplicate
 * and no double-installed `document` listener (the listener itself is
 * lazily installed on the first push and torn down once the stack is empty,
 * ref-counted by the stack's own length rather than a separate counter).
 */

// Additive widening (bead terra-design-v2-drilldowns-ppn.22, U14): callers
// that need to distinguish a real Escape from one a nested Base UI popup
// already handled (`e.defaultPrevented`) or ignore Escape while typing
// (`e.target`) now receive the event. Every existing caller declares its
// callback with zero parameters (`() => ...`), which remains a valid
// `(e: KeyboardEvent) => void` — TypeScript allows a function type with
// fewer parameters than the caller passes — so this is source- and
// type-compatible with all six current callers.
type EscapeCallback = (e: KeyboardEvent) => void;

interface EscapeLayer {
  id: number;
  onEscape: EscapeCallback;
}

let stack: EscapeLayer[] = [];
let nextId = 0;
let listenerInstalled = false;

function handleKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  const top = stack[stack.length - 1];
  top?.onEscape(e);
}

function ensureListenerInstalled(): void {
  if (listenerInstalled) return;
  document.addEventListener('keydown', handleKeyDown);
  listenerInstalled = true;
}

function teardownListenerIfEmpty(): void {
  if (stack.length > 0 || !listenerInstalled) return;
  document.removeEventListener('keydown', handleKeyDown);
  listenerInstalled = false;
}

/**
 * Registers a layer that should own the next Escape keypress while it's the
 * topmost registered layer. Returns an unregister function — call it from
 * the same effect's cleanup. Removal is by the layer's own id, not a blind
 * stack pop, so unregistering is safe regardless of push/pop ordering
 * (including StrictMode's double-invoke).
 */
export function pushEscapeLayer(onEscape: EscapeCallback): () => void {
  const id = ++nextId;
  stack.push({ id, onEscape });
  ensureListenerInstalled();
  return () => {
    stack = stack.filter(layer => layer.id !== id);
    teardownListenerIfEmpty();
  };
}
